// ゲーム（iframe）からの頼みを受けて、セーブ・ランキング・広告・計測を代わりに行う。
// ゲームは信用しない：送り主（origin と window）・中身の形・回数を必ず確かめ、合わない物は捨てる。
import {
  isProtocolMessage,
  LIMITS,
  METHOD_NAMES,
  PROTOCOL,
  type ErrorCode,
  type EventMessage,
  type EventName,
  type Events,
  type Json,
  type MethodName,
  type Methods,
  type Period,
  type RequestMessage,
  type ResultParams,
  type StampId,
  type SubmitResult,
} from "@appful/sdk/protocol";
import { ads } from "./ads";
import * as ai from "./ai";
import { track } from "./analytics";
import { lobbySuspendedUntil, userInfo, whenReady, whenSignedIn } from "./auth";
import type { CatalogBoard, CatalogGame } from "./catalog";
import { config } from "./config";
import { getLocale } from "./i18n";
import * as leaderboard from "./leaderboard";
import { isMuted } from "./local";
import { OnlineClient, type RoomPlayer } from "./online";
import { listBlocks } from "./safety";
import * as saves from "./saves";

export interface BridgeHooks {
  /** ゲームがログインの画面を頼んだ。閉じたら resolve する */
  requestSignIn(): Promise<void>;
  onLoadingFinished(): void;
  /** セーブが書けた（上の帯に「保存しました」と出す） */
  onSaved(cloud: boolean): void;
  /** 終わった後の画面を出す。利用者が「もう一回」を押したら resolve する */
  showResult(params: ResultParams, submit: SubmitResult | null, board: CatalogBoard | null): Promise<void>;
  /** 対戦の部屋の顔ぶれが変わった（me が -1 なら部屋を出た） */
  onRoster(players: RoomPlayer[], me: number): void;
  /** ほかの人がスタンプを送った */
  onStamp(from: RoomPlayer | undefined, id: StampId): void;
  /** 対戦がお休み中（until まで）。知らせの窓を出す */
  onSuspended(until: Date): void;
}

class BridgeError extends Error {
  constructor(public code: ErrorCode, message: string) {
    super(message);
  }
}

const bad = (msg: string) => new BridgeError("bad_request", msg);

// 1分あたりの上限（ゲームの不具合や悪意で、保存や関数を呼び続けさせない）
const RATE: Record<string, number> = {
  save: 120,
  "leaderboard.submit": 10,
  "leaderboard.top": 30,
  ads: 10,
  "result.show": 20,
  "events.track": 60,
  // 対戦の送り。部屋の側でも1秒に30回で止める
  "online.send": 1_800,
  "online.join": 20,
  // AI。関数の側でも、ログインした人は 1 分 20 回・ゲストは IP ごとに 1 分 10 回で止める（docs/AI.md）
  "ai.generate": 20,
  other: 120,
};
const rateGroup = (m: MethodName) =>
  m.startsWith("save.") ? "save" : m.startsWith("ads.") ? "ads" : m in RATE ? m : "other";

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** JSON にできる値か（深さと大きさも確かめる） */
function isJson(v: unknown, depth = 0): v is Json {
  if (depth > 20) return false;
  if (v === null || typeof v === "boolean" || typeof v === "string") return true;
  if (typeof v === "number") return Number.isFinite(v);
  if (Array.isArray(v)) return v.every((x) => isJson(x, depth + 1));
  if (isPlainRecord(v)) return Object.values(v).every((x) => isJson(x, depth + 1));
  return false;
}

export class GameBridge {
  private frame: HTMLIFrameElement;
  private game: CatalogGame;
  private hooks: BridgeHooks;
  private counts = new Map<string, { windowStart: number; n: number }>();
  private initialized = false;
  private playing = false;
  private startedAt = performance.now();
  private lastSignInPrompt = 0;
  private readonly onMessage = (ev: MessageEvent) => void this.handle(ev);
  private online: OnlineClient;

  constructor(frame: HTMLIFrameElement, game: CatalogGame, hooks: BridgeHooks, private playOrigin: string) {
    this.frame = frame;
    this.game = game;
    this.hooks = hooks;
    this.online = new OnlineClient(game.id, {
      message: (from, data) => this.send("online.message", { from, data }),
      player: (seat, connected) => this.send("online.player", { seat, connected }),
      host: (seat) => this.send("online.host", { seat }),
      stamp: (from, id) => {
        this.hooks.onStamp(this.online.players.find((p) => p.seat === from), id);
        this.send("online.stamp", { from, id });
      },
      closed: (reason) => this.send("online.closed", { reason }),
      roster: (players, me) => this.hooks.onRoster(players, me),
    });
    window.addEventListener("message", this.onMessage);
  }

  /** 一覧の帯のスタンプのボタンから送る */
  sendStamp(id: StampId) {
    this.online.stamp(id);
    this.send("online.stamp", { from: this.online.me, id });
  }

  destroy() {
    this.online.leave();
    window.removeEventListener("message", this.onMessage);
    if (this.playing) track("gameplay_stop", { game_id: this.game.id, reason: "leave" });
  }

  send<E extends EventName>(event: E, data: Events[E]) {
    if (!this.initialized) return;
    const msg: EventMessage<E> = { protocol: PROTOCOL, kind: "event", event, data };
    this.frame.contentWindow?.postMessage(msg, this.playOrigin);
  }

  private limited(method: MethodName): boolean {
    const g = rateGroup(method);
    const now = Date.now();
    const c = this.counts.get(g);
    if (!c || now - c.windowStart > 60_000) {
      this.counts.set(g, { windowStart: now, n: 1 });
      return false;
    }
    c.n++;
    return c.n > (RATE[g] ?? RATE.other!);
  }

  private async handle(ev: MessageEvent) {
    // 自分の iframe の、遊ぶ場所の住所からの物だけを受ける。
    if (ev.origin !== this.playOrigin || ev.source !== this.frame.contentWindow) return;
    if (!isProtocolMessage(ev.data) || ev.data.kind !== "request") return;
    const req = ev.data as Partial<RequestMessage>;
    if (typeof req.id !== "number" || !Number.isSafeInteger(req.id)) return;
    const reply = (body: { ok: true; result: unknown } | { ok: false; error: { code: ErrorCode; message: string } }) =>
      (ev.source as WindowProxy).postMessage({ protocol: PROTOCOL, kind: "response", id: req.id, ...body }, this.playOrigin);
    try {
      if (typeof req.method !== "string" || !METHOD_NAMES.includes(req.method as MethodName)) {
        throw new BridgeError("unknown_method", `unknown method: ${String(req.method).slice(0, 40)}`);
      }
      const method = req.method as MethodName;
      if (method !== "init" && !this.initialized) throw new BridgeError("not_ready", "call init first");
      if (this.limited(method)) throw new BridgeError("rate_limited", `${method}: too many calls`);
      const params: unknown = req.params ?? {};
      if (!isPlainRecord(params)) throw bad("params must be an object");
      // 前回のログインの復元が済むまで待つ（済む前に読むと、ゲストのセーブを返してしまう）。
      await whenReady();
      const result = await this.dispatch(method, params);
      reply({ ok: true, result });
    } catch (e) {
      const code: ErrorCode =
        e instanceof BridgeError || e instanceof ai.AiError ? e.code : (e as { code?: string }).code === "too_large" ? "too_large" : "internal";
      if (code === "internal") console.error("[bridge]", e);
      reply({ ok: false, error: { code, message: e instanceof Error ? e.message : "error" } });
    }
  }

  private board(boardId: unknown) {
    if (typeof boardId !== "string" || !LIMITS.boardIdPattern.test(boardId)) throw bad("boardId");
    const b = this.game.leaderboards.find((x) => x.id === boardId);
    if (!b) throw bad(`unknown board: ${boardId}`);
    return b;
  }

  private key(k: unknown): string {
    if (typeof k !== "string" || !LIMITS.keyPattern.test(k)) throw bad("key must match [A-Za-z0-9_.-]{1,64}");
    return k;
  }

  private async dispatch(method: MethodName, p: Record<string, unknown>): Promise<Methods[MethodName]["result"]> {
    const gameId = this.game.id;
    switch (method) {
      case "init":
        this.initialized = true;
        return { gameId, locale: getLocale(), user: userInfo(), muted: isMuted(), sdkVersion: String(p.sdkVersion ?? "") };
      case "lifecycle.loadingFinished":
        track("game_ready", { game_id: gameId, load_ms: Math.round(performance.now() - this.startedAt) });
        this.hooks.onLoadingFinished();
        return null;
      case "lifecycle.gameplayStart":
        if (!this.playing) track("gameplay_start", { game_id: gameId });
        this.playing = true;
        return null;
      case "lifecycle.gameplayStop":
        if (this.playing) track("gameplay_stop", { game_id: gameId });
        this.playing = false;
        return null;
      case "ads.interstitial":
      case "ads.rewarded": {
        // 遊んでいる最中の全画面の広告は出さない（区切りでだけ）。
        if (method === "ads.interstitial" && this.playing) return { shown: false };
        const type = method === "ads.interstitial" ? "interstitial" : "rewarded";
        track("ad_request", { game_id: gameId, type });
        this.send("pause", {});
        try {
          if (method === "ads.interstitial") {
            const r = await ads.interstitial({ gameId });
            if (r.shown) track("ad_shown", { game_id: gameId, type });
            return r;
          }
          const r = await ads.rewarded({ gameId });
          track(r.granted ? "ad_reward_granted" : "ad_reward_skipped", { game_id: gameId });
          return r;
        } finally {
          this.send("resume", {});
        }
      }
      case "save.get":
        return { value: await saves.getValue(gameId, this.key(p.key)) };
      case "save.set": {
        const key = this.key(p.key);
        if (!isJson(p.value)) throw bad("value must be JSON (finite numbers, no functions)");
        const r = await saves.setValue(gameId, key, p.value, this.game.length === "long" ? LIMITS.saveBytesLong : LIMITS.saveBytes);
        this.hooks.onSaved(r.cloud);
        return r;
      }
      case "save.remove":
        return saves.removeValue(gameId, this.key(p.key));
      case "result.show": {
        if (typeof p.score !== "number" || !Number.isFinite(p.score)) throw bad("score must be a number");
        const board = p.boardId === undefined ? null : this.board(p.boardId);
        if (p.message !== undefined && typeof p.message !== "string") throw bad("message must be a string");
        const params: ResultParams = {
          score: p.score,
          ...(board ? { boardId: board.id } : {}),
          ...(typeof p.message === "string" ? { message: p.message.slice(0, LIMITS.resultMessageLength) } : {}),
        };
        // 結果の画面は「区切り」。遊んでいる扱いを終わらせる。
        if (this.playing) track("gameplay_stop", { game_id: gameId });
        this.playing = false;
        const submit = board ? await leaderboard.submit(gameId, board, p.score) : null;
        track("result_show", { game_id: gameId, score: p.score, signed_in: userInfo().signedIn });
        await this.hooks.showResult(params, submit, board);
        // 「もう一回」のあと：前の広告から間隔が空いていれば、ここで全画面の広告を出す（ゲームからは頼まない）
        this.send("pause", {});
        try {
          const ad = await ads.interstitial({ gameId });
          if (ad.shown) track("ad_shown", { game_id: gameId, type: "interstitial" });
        } finally {
          this.send("resume", {});
        }
        track("result_retry", { game_id: gameId });
        return { action: "retry" };
      }
      case "leaderboard.submit": {
        const board = this.board(p.boardId);
        if (typeof p.score !== "number" || !Number.isFinite(p.score)) throw bad("score must be a number");
        return leaderboard.submit(gameId, board, p.score);
      }
      case "leaderboard.top": {
        const board = this.board(p.boardId);
        const period: Period = p.period === "daily" ? "daily" : p.period === undefined || p.period === "all" ? "all" : (() => { throw bad("period"); })();
        if (!board.periods.includes(period)) throw bad(`period ${period} is not enabled for ${board.id}`);
        const n = p.limit === undefined ? 10 : Number(p.limit);
        if (!Number.isInteger(n) || n < 1 || n > LIMITS.leaderboardLimit) throw bad("limit must be 1-50");
        return { entries: await leaderboard.top(gameId, board, period, n) };
      }
      case "user.get":
        return userInfo();
      case "user.requestSignIn":
        // ゲームがログインの画面を出し続けて、利用者の操作を邪魔しないように、10 秒に1回まで。
        if (!userInfo().signedIn && Date.now() - this.lastSignInPrompt > 10_000) {
          this.lastSignInPrompt = Date.now();
          await this.hooks.requestSignIn();
        }
        return userInfo();
      case "events.track": {
        const name = p.name;
        if (typeof name !== "string" || !LIMITS.eventNamePattern.test(name)) throw bad("event name must match [a-z][a-z0-9_]{0,39}");
        const extra = p.params === undefined ? {} : p.params;
        if (!isPlainRecord(extra) || Object.keys(extra).length > LIMITS.eventParams) throw bad("params: up to 10 entries");
        const clean: Record<string, string | number | boolean> = {};
        for (const [k, v] of Object.entries(extra)) {
          if (!/^[a-z][a-z0-9_]{0,39}$/.test(k)) throw bad(`param name: ${k.slice(0, 40)}`);
          if (typeof v === "string") clean[`p_${k}`] = v.slice(0, LIMITS.eventStringLength);
          else if (typeof v === "boolean" || (typeof v === "number" && Number.isFinite(v))) clean[`p_${k}`] = v;
          else throw bad(`param ${k}: string, number or boolean`);
        }
        track("game_event", { game_id: gameId, event_name: name, ...clean });
        return null;
      }
      case "online.join": {
        const mode = p.mode;
        if (typeof mode !== "string" || !LIMITS.modeIdPattern.test(mode)) throw bad("mode");
        if (!this.game.online?.modes.some((m) => m.id === mode)) throw bad(`unknown mode: ${mode}`);
        if (!config.roomsOrigin) return { status: "unavailable" };
        // 対戦は相手と取り違えないよう、ログインした人だけ。まだなら、ログインの画面を1回出す
        if (!userInfo().signedIn) {
          await this.hooks.requestSignIn();
          if (!(await whenSignedIn())) return { status: "signin_required" };
        }
        // お休み中なら、理由を知らせて組まない（部屋の Worker も断る）。ゲームには「いまは対戦できない」と返す
        const suspended = await lobbySuspendedUntil();
        if (suspended) {
          track("online_not_matched", { game_id: gameId, mode, status: "suspended" });
          this.hooks.onSuspended(suspended);
          return { status: "unavailable" };
        }
        const blocks = await listBlocks().catch(() => []);
        track("online_join", { game_id: gameId, mode });
        const r = await this.online.join(mode, blocks);
        track(r.status === "matched" ? "online_matched" : "online_not_matched", { game_id: gameId, mode, status: r.status });
        return r;
      }
      case "online.leave":
        this.online.leave();
        return null;
      case "online.send": {
        if (!isJson(p.data) || JSON.stringify(p.data).length > LIMITS.onlineMessageBytes) throw bad("data must be JSON up to 2KB");
        if (p.to !== undefined && !(Number.isInteger(p.to) && (p.to as number) >= 0 && (p.to as number) < LIMITS.onlinePlayers)) throw bad("to must be a seat number");
        this.online.send(p.data, p.to as number | undefined);
        return null;
      }
      case "online.result": {
        const pl = p.placements;
        if (!Array.isArray(pl) || pl.length > LIMITS.onlinePlayers || !pl.every((x) => Number.isInteger(x) && x >= 1)) throw bad("placements must be positive integers per seat");
        return this.online.result(pl as number[]);
      }
      case "ai.generate": {
        // ゲームの id は、この枠で開いているゲームの物（gameId）を使う。ゲームが送った値に gameId があっても読まない
        const { prompt, system, schema, maxTokens } = p;
        if (typeof prompt !== "string" || !prompt.trim()) throw bad("prompt must be a non-empty string");
        if (prompt.length > LIMITS.aiPromptChars) throw new BridgeError("too-long", `prompt: up to ${LIMITS.aiPromptChars} characters`);
        if (system !== undefined && typeof system !== "string") throw bad("system must be a string");
        if (typeof system === "string" && system.length > LIMITS.aiSystemChars) throw new BridgeError("too-long", `system: up to ${LIMITS.aiSystemChars} characters`);
        if (schema !== undefined && !(isPlainRecord(schema) && isJson(schema))) throw bad("schema must be a JSON object");
        if (schema !== undefined && JSON.stringify(schema).length > LIMITS.aiSchemaChars) throw new BridgeError("too-long", `schema: up to ${LIMITS.aiSchemaChars} characters of JSON`);
        if (maxTokens !== undefined && (typeof maxTokens !== "number" || !Number.isFinite(maxTokens))) throw bad("maxTokens must be a number");
        track("ai_request", { game_id: gameId });
        try {
          const r = await ai.generate(
            gameId,
            {
              prompt,
              ...(typeof system === "string" ? { system } : {}),
              ...(schema !== undefined ? { schema: schema as { [key: string]: Json } } : {}),
              ...(typeof maxTokens === "number" ? { maxTokens } : {}),
            },
            () => this.hooks.requestSignIn(),
          );
          return r;
        } catch (e) {
          track("ai_failed", { game_id: gameId, code: e instanceof ai.AiError ? e.code : "unavailable" });
          throw e;
        }
      }
    }
  }
}
